import * as React from 'react'
import { Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Picker from '@components/picker/Picker'
import Animators, { Animation } from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as LocationStore from '@store/location/location'

import { FormikHelpers, useFormik } from 'formik'

import { findLocalisationSubLevels } from '../utils'

interface Values {
  location: string
  subLocation: string
  subsubLocation: string
}

const Location = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      location: form.location,
      subLocation: form.subLocation,
      subsubLocation: form.subsubLocation,
      space: form.space,
    }),
    [form.location, form.subLocation, form.subsubLocation, form.space]
  )

  const i18n = useI18n()

  const locations = useReducer(LocationStore.store, (s) => s.places)
  const spaces = React.useMemo(() => locations.filter((l) => l.enabled), [locations])

  const space = spaces.find((p) => p.id === form.space)
  const subs = findLocalisationSubLevels(spaces, space)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, handleSubmit, setValues, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  const onLocation = (next: string) => {
    setValues({
      ...values,
      location: next,
      subLocation: '',
      subsubLocation: '',
    })
  }

  const onSubLocation = (next: string) => {
    setValues({
      ...values,
      subLocation: next,
      subsubLocation: '',
    })
  }

  const onSearch = (next: string) => {
    const item = spaces.find((p) => p.id === next)
    const parent = spaces.find((p) => !!item && p.id === item.parentId)
    const parent2 = spaces.find((p) => !!parent && p.id === parent.parentId)
    const parent3 = spaces.find((p) => !!parent2 && p.id === parent2.parentId)

    const items = [parent3, parent2, parent, item].filter(Boolean) as LocalisationResponse[]

    // ignore first item as we can't change
    setValues({
      ...values,
      location: items[1] ? items[1].id : '',
      subLocation: items[2] ? items[2].id : '',
      subsubLocation: items[3] ? items[3].id : '',
    })
  }

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  if (!space) {
    return null
  }

  const spaceOpt = { label: space.libelle, value: space.id }

  const options = spaceOpt
    ? spaces.filter((p) => p.parentId === spaceOpt.value).map((p) => ({ label: p.libelle, value: p.id }))
    : []
  const opt = options.find((o) => values.location === o.value)

  const subOptions = opt
    ? spaces.filter((p) => p.parentId === opt.value).map((p) => ({ label: p.libelle, value: p.id }))
    : []
  const subOpt = subOptions.find((o) => values.subLocation === o.value)

  const subsubOptions = subOpt
    ? spaces.filter((p) => p.parentId === subOpt.value).map((p) => ({ label: p.libelle, value: p.id }))
    : []
  const subsubOpt = subsubOptions.find((o) => values.subsubLocation === o.value)

  const fullOptions = subs.map((p) => ({ label: p.libelle, value: p.id }))

  return (
    <Animators>
      <MainContainer>
        {/* position inverted to satisfy picker */}
        {/* subsubLocation */}
        <Animation>
          <Title disabled={!subOpt}>{i18n.t('screens.incident.LOCATION.subSubLocation')}</Title>
          <Picker
            options={subsubOptions}
            value={subsubOpt}
            onChange={(next) => setFieldValue('subsubLocation', next ? next.value : '')}
            placeholder={i18n.t('screens.incident.LOCATION.placeholder')}
            disabled={!subOpt}
            title={i18n.t('screens.incident.LOCATION.subSubLocation')}
          />
        </Animation>
        {/* sub-location */}
        <Animation>
          <Title disabled={!opt || subOptions.length === 0}>{i18n.t('screens.incident.LOCATION.subLocation')}</Title>
          <Picker
            options={subOptions}
            value={subOpt}
            onChange={(next) => onSubLocation(next ? next.value : '')}
            placeholder={i18n.t('screens.incident.LOCATION.placeholder')}
            disabled={!opt || subOptions.length === 0}
            title={i18n.t('screens.incident.LOCATION.subLocation')}
          />
        </Animation>
        {/* location */}
        <Animation>
          <Title disabled={options.length === 0}>{i18n.t('screens.incident.LOCATION.location')}</Title>
          <Picker
            options={options}
            value={opt}
            onChange={(next) => onLocation(next ? next.value : '')}
            placeholder={i18n.t('screens.incident.LOCATION.placeholder')}
            disabled={options.length === 0}
            title={i18n.t('screens.incident.LOCATION.location')}
          />
        </Animation>

        <Animation>
          <SearchContainer>
            <Picker
              options={fullOptions}
              onChange={(next) => onSearch(next ? next.value : '')}
              placeholder={i18n.t('common.search')}
              disabled={options.length === 0}
              indicator={false}
              value={{ label: i18n.t('screens.incident.LOCATION.search'), value: '' }}
              title={i18n.t('screens.incident.LOCATION.search')}
              isSearchable
            />
          </SearchContainer>
        </Animation>

        {/* space */}
        <Animation>
          <Title>{i18n.t('screens.incident.LOCATION.space')}</Title>
          <Picker options={[]} value={spaceOpt} onChange={() => null} disabled />
        </Animation>
      </MainContainer>
    </Animators>
  )
}

export default Location

const MainContainer = styled(View)`
  margin: 0 20px;
  flex-direction: column-reverse;
`

const Title = styled(Text)<{ disabled?: boolean }>`
  ${(props) => props.theme.fonts.Headline};
  opacity: ${(props) => (props.disabled ? 0.2 : 1)};
  margin-bottom: 5px;
  margin-top: 15px;
`

const SearchContainer = styled(View)`
  margin-top: 50px;
  margin-bottom: 25px;
`
